import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getMe } from '../api/client';
import type { UserMe } from '../api/types';
import { navigate, ROUTE_CHANGE_EVENT } from '../router';
import { boxSizingStyles } from '../styles/lit-shared';

function isNavActive(pathname: string, basePath: string): boolean {
  if (pathname === basePath) return true;
  return pathname.startsWith(`${basePath}/`);
}

@customElement('app-shell')
export class AppShell extends LitElement {
  @state() private user: UserMe | null = null;
  @state() private currentPath = location.pathname;
  @state() private navOpen = false;

  connectedCallback() {
    super.connectedCallback();
    void this.checkAuth();
    window.addEventListener(ROUTE_CHANGE_EVENT, this._onRouteChange);
    window.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(ROUTE_CHANGE_EVENT, this._onRouteChange);
    window.removeEventListener('keydown', this._onKeyDown);
  }

  private _onRouteChange = (e: Event) => {
    const pathname = (e as CustomEvent<{ pathname: string }>).detail?.pathname;
    this.currentPath = pathname ?? location.pathname;
    this.navOpen = false;
    void this.checkAuth();
  };

  private _onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.navOpen) {
      this.navOpen = false;
    }
  };

  private closeNav() {
    this.navOpen = false;
  }

  private toggleNav() {
    this.navOpen = !this.navOpen;
  }

  private async checkAuth() {
    try {
      this.user = await getMe();
    } catch {
      this.user = null;
    }
  }

  static styles = [
    boxSizingStyles,
    css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100dvh;
    }

    header {
      position: sticky;
      top: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-md);
      min-width: 0;
      overflow: hidden;
      height: var(--header-height);
      padding: 0 var(--space-lg);
      background: rgb(6 6 15 / 72%);
      backdrop-filter: blur(16px) saturate(160%);
      -webkit-backdrop-filter: blur(16px) saturate(160%);
      border-bottom: 1px solid var(--color-border);
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      min-width: 0;
      flex-shrink: 0;
    }

    .brand {
      display: inline-flex;
      align-items: center;
      gap: 0.625rem;
      font-size: 1.0625rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      text-decoration: none;
      transition: opacity var(--duration-fast) var(--ease-out);
      min-width: 0;
    }

    .brand:hover {
      opacity: 0.9;
      text-decoration: none;
    }

    .brand-mark {
      display: block;
      width: 2rem;
      height: 2rem;
      object-fit: contain;
      flex-shrink: 0;
      border-radius: var(--radius-sm);
    }

    .brand-name {
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .menu-btn {
      display: none;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      padding: 0;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      background: var(--color-surface-hover);
      color: var(--color-text);
      cursor: pointer;
      flex-shrink: 0;
      transition:
        border-color var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    .menu-btn:hover {
      border-color: var(--color-border-glow);
    }

    .menu-btn svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    main {
      flex: 1;
      width: 100%;
      max-width: var(--max-width);
      margin: 0 auto;
      padding: var(--space-xl) var(--space-lg);
    }

    footer {
      padding: var(--space-lg);
      text-align: center;
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      border-top: 1px solid var(--color-border);
      background: rgb(6 6 15 / 40%);
    }

    .footer-tagline {
      word-break: break-word;
    }

    footer span {
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      font-weight: 600;
    }

    .footer-legal {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: var(--space-xs) var(--space-sm);
      margin-bottom: var(--space-sm);
    }

    .footer-link {
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      text-decoration: none;
      cursor: pointer;
      border: none;
      background: transparent;
      padding: 0;
      transition: color var(--duration-fast) var(--ease-out);
    }

    .footer-link:hover {
      color: var(--color-text-muted);
    }

    .footer-sep {
      color: var(--color-text-subtle);
      opacity: 0.5;
      user-select: none;
    }

    .nav-desktop {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
      min-width: 0;
    }

    .nav-link {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.75rem;
      border-radius: var(--radius-sm);
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--color-text-muted);
      text-decoration: none;
      cursor: pointer;
      border: none;
      background: transparent;
      transition:
        color var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    .nav-link:hover {
      color: var(--color-text);
      background: var(--color-surface-hover);
    }

    .nav-link--active {
      color: var(--color-primary-light);
      background: rgb(139 92 246 / 10%);
    }

    .nav-drawer-link {
      display: flex;
      align-items: center;
      width: 100%;
      padding: var(--space-sm) var(--space-md);
      border-radius: var(--radius-sm);
      font-size: 1rem;
      font-weight: 500;
      color: var(--color-text-muted);
      text-align: left;
      cursor: pointer;
      border: none;
      background: transparent;
      transition:
        color var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    .nav-drawer-link:hover {
      color: var(--color-text);
      background: var(--color-surface-hover);
    }

    .nav-drawer-link--active {
      color: var(--color-primary-light);
      background: rgb(139 92 246 / 10%);
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      min-width: 0;
      flex-shrink: 0;
    }

    .user-widget {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }

    .user-avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      background: var(--color-surface-hover);
      border: 1px solid var(--color-border);
      cursor: pointer;
      transition: border-color var(--duration-fast) var(--ease-out);
      flex-shrink: 0;
    }

    .user-avatar:hover {
      border-color: var(--color-primary-light);
    }

    .user-avatar svg {
      width: 1.125rem;
      height: 1.125rem;
      color: var(--color-text-muted);
    }

    .user-email {
      font-size: 0.8125rem;
      color: var(--color-text-muted);
      max-width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;
    }

    .user-email:hover {
      color: var(--color-text);
    }

    .credits-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.625rem;
      border-radius: var(--radius-full);
      background: var(--gradient-primary-soft);
      border: 1px solid var(--color-border-glow);
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--color-primary-light);
      cursor: pointer;
      flex-shrink: 0;
      transition:
        background var(--duration-fast) var(--ease-out),
        transform var(--duration-fast) var(--ease-out);
    }

    .credits-btn:hover {
      transform: translateY(-1px);
    }

    .nav-overlay {
      display: none;
      position: fixed;
      inset: 0;
      z-index: 150;
      background: rgb(0 0 0 / 55%);
      border: none;
      padding: 0;
      cursor: pointer;
    }

    .nav-drawer {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 200;
      width: min(280px, 85vw);
      height: 100dvh;
      padding: var(--space-lg) var(--space-md);
      background: var(--palette-ink-900);
      border-right: 1px solid var(--color-border);
      box-shadow: var(--shadow-lg);
      flex-direction: column;
      gap: var(--space-md);
      transform: translateX(-100%);
      transition: transform var(--duration-normal) var(--ease-out);
    }

    .nav-drawer--open {
      transform: translateX(0);
    }

    .nav-drawer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: var(--space-sm);
      border-bottom: 1px solid var(--color-border);
    }

    .nav-drawer-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--color-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .nav-drawer-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      padding: 0;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--color-text-muted);
      cursor: pointer;
    }

    .nav-drawer-close:hover {
      color: var(--color-text);
      border-color: var(--color-border-strong);
    }

    .nav-drawer-close svg {
      width: 1.125rem;
      height: 1.125rem;
    }

    .nav-drawer-nav {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .nav-drawer-user {
      margin-top: auto;
      padding-top: var(--space-md);
      border-top: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
    }

    .nav-drawer-email {
      font-size: 0.8125rem;
      color: var(--color-text-muted);
      word-break: break-all;
    }

    .nav-drawer-actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-sm);
    }

    @media (max-width: 639px) {
      header {
        padding: 0 var(--space-md);
      }

      main {
        padding: var(--space-lg) var(--space-md);
      }

      footer {
        padding-inline: var(--space-md);
      }

      .menu-btn {
        display: flex;
      }

      .nav-desktop {
        display: none;
      }

      .nav-overlay {
        display: block;
      }

      .nav-overlay:not(.nav-overlay--visible) {
        pointer-events: none;
        opacity: 0;
      }

      .nav-overlay--visible {
        opacity: 1;
        transition: opacity var(--duration-normal) var(--ease-out);
      }

      .nav-drawer {
        display: flex;
      }

      .user-email {
        display: none;
      }
    }
  `,
  ];

  private navLink(path: string, label: string, drawer = false) {
    const active = isNavActive(this.currentPath, path);
    const cls = drawer
      ? `nav-drawer-link ${active ? 'nav-drawer-link--active' : ''}`
      : `nav-link ${active ? 'nav-link--active' : ''}`;

    return html`
      <button
        class=${cls}
        aria-current=${active ? 'page' : 'false'}
        @click=${() => {
          this.closeNav();
          void navigate(path);
        }}
      >
        ${label}
      </button>
    `;
  }

  private footerLink(path: string, label: string) {
    return html`
      <button class="footer-link" type="button" @click=${() => void navigate(path)}>
        ${label}
      </button>
    `;
  }

  private renderUserWidget(compact = false) {
    if (!this.user) return '';

    const totalCredits = this.user.credits + this.user.extraCredits;

    return html`
      <div class="user-widget">
        ${compact
          ? ''
          : html`
            <span
              class="user-email"
              @click=${() => void navigate('/profile')}
              title=${this.user!.email}
            >${this.user!.email}</span>
          `}
        <button
          class="user-avatar"
          @click=${() => void navigate('/profile')}
          title="Mi perfil"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </button>
        <button
          class="credits-btn"
          @click=${() => void navigate('/credits')}
          title="Gestionar créditos"
        >
          ${totalCredits} cred.
        </button>
      </div>
    `;
  }

  private renderNavDrawer() {
    if (!this.user) return '';

    return html`
      <button
        class="nav-overlay ${this.navOpen ? 'nav-overlay--visible' : ''}"
        type="button"
        aria-label="Cerrar menú"
        @click=${this.closeNav}
      ></button>
      <aside
        id="nav-drawer"
        class="nav-drawer ${this.navOpen ? 'nav-drawer--open' : ''}"
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
        aria-hidden=${this.navOpen ? 'false' : 'true'}
      >
        <div class="nav-drawer-header">
          <span class="nav-drawer-title">Menú</span>
          <button
            class="nav-drawer-close"
            type="button"
            aria-label="Cerrar menú"
            @click=${this.closeNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <nav class="nav-drawer-nav" aria-label="Navegación principal">
          ${this.navLink('/dashboard', 'Dashboard', true)}
          ${this.navLink('/content', 'Contenido', true)}
          ${this.navLink('/campaigns', 'Campañas', true)}
        </nav>
        <div class="nav-drawer-user">
          <span class="nav-drawer-email">${this.user.email}</span>
          <div class="nav-drawer-actions">
            <button
              class="credits-btn"
              @click=${() => {
                this.closeNav();
                void navigate('/credits');
              }}
            >
              ${this.user.credits + this.user.extraCredits} créditos
            </button>
            <button
              class="nav-drawer-link"
              @click=${() => {
                this.closeNav();
                void navigate('/profile');
              }}
            >
              Mi perfil
            </button>
          </div>
        </div>
      </aside>
    `;
  }

  render() {
    return html`
      <header>
        <div class="header-left">
          ${this.user
            ? html`
              <button
                class="menu-btn"
                type="button"
                aria-expanded=${this.navOpen ? 'true' : 'false'}
                aria-controls="nav-drawer"
                aria-label=${this.navOpen ? 'Cerrar menú' : 'Abrir menú'}
                @click=${this.toggleNav}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              </button>
            `
            : ''}
          <a
            class="brand"
            href="/dashboard"
            @click=${(e: Event) => {
              e.preventDefault();
              void navigate('/dashboard');
            }}
          >
            <img
              class="brand-mark"
              src="/logo_blunticle.png"
              alt=""
              width="379"
              height="424"
              aria-hidden="true"
            />
            <span class="brand-name">Blunticle</span>
          </a>
        </div>

        ${this.user
          ? html`
            <nav class="nav-desktop" aria-label="Navegación principal">
              ${this.navLink('/dashboard', 'Dashboard')}
              ${this.navLink('/content', 'Contenido')}
              ${this.navLink('/campaigns', 'Campañas')}
            </nav>
          `
          : ''}

        <div class="header-right">
          ${this.renderUserWidget()}
        </div>
      </header>

      ${this.renderNavDrawer()}

      <main>
        <div id="outlet"></div>
      </main>
      <footer>
        <nav class="footer-legal" aria-label="Enlaces legales">
          ${this.footerLink('/politica-privacidad', 'Política de Privacidad')}
          <span class="footer-sep" aria-hidden="true">·</span>
          ${this.footerLink('/aviso-legal', 'Aviso Legal')}
          <span class="footer-sep" aria-hidden="true">·</span>
          ${this.footerLink('/politica-cookies', 'Política de Cookies')}
        </nav>
        <p class="footer-tagline">
          <span>Blunticle</span> &mdash; Generación de contenido como Micro Empresa Virtual basada en Agentes de IA.
        </p>
      </footer>
      <cookie-consent></cookie-consent>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-shell': AppShell;
  }
}
